import React, {createContext, useContext, useState} from 'react';
import { RestfulProvider } from 'restful-react';
import { jwtDecode as decodeJWT } from 'jwt-decode';
import dynamicConfig from '../config/dynamicConfig';

interface DecodedJWT {
    exp: number;
    [key: string]: any;
}

interface ApiErrorContextInterface {
    apiError: boolean,
    setApiError: (apiError: boolean) => void;
}

// Store api token globally and clear it when it expires
let token: string|null;
let refetchTokenTimeout: number;
let refetchTokenPromise: Promise<void>;
function setToken(newToken: string|null, expiresIn?: number) {
    token = newToken;

    if (token) {
        localStorage.setItem('token', token);

        // Set clear timeout
        expiresIn = expiresIn ? expiresIn * 1000 : (decodeJWT(token) as DecodedJWT).exp * 1000 - Date.now();
        if (expiresIn <= 0) {
            // If it is already expired, recall the current method and set the token to null
            setToken(null);
        } else {
            // Else wait for it to expire
            window.clearTimeout(refetchTokenTimeout);
            refetchTokenTimeout = window.setTimeout(() => setToken(null), expiresIn);
        }
    } else {
        window.clearTimeout(refetchTokenTimeout);
        localStorage.removeItem('token');
    }
}

// Set initial token with the value saved in the local storage
setToken(localStorage.getItem('token'));

// Make api errors accessible from everywhere
export const ApiErrorContext = createContext({} as ApiErrorContextInterface)

export const useAtmApiRequestOptions = () => {
    const {setApiError} = useContext(ApiErrorContext)

    const headers: HeadersInit = {};

    // Return async function to fetch token right before the actual request
    return async () => {
        if (refetchTokenPromise) {
            // If there is already an ongoing refetch, wait for it
            await refetchTokenPromise;
        }

        if (!token) {
            // Set global promise so all other requests wait for us
            refetchTokenPromise = new Promise(async (resolve, reject) => {
                try {
                    // Create login data
                    const formData = new URLSearchParams();
                    formData.append('grant_type', 'client_credentials');
                    formData.append('client_id', dynamicConfig.clientId);
                    formData.append('client_secret', dynamicConfig.clientSecret);

                    // Get token
                    const response = await fetch(dynamicConfig.tokenUrl, {
                        method: 'POST',
                        mode: 'cors',
                        body: formData,
                    });

                    const {expires_in, access_token} = await response.json();

                    setToken(access_token, expires_in);

                    resolve();
                } catch (error) {
                    setApiError(true);

                    reject(error);
                }
            });
            await refetchTokenPromise;
        }

        headers['Authorization'] = 'Bearer ' + token;

        return {
            headers,
        };
    }
};

type ApiProviderProps = {
    children: React.ReactNode;
}

const ApiProvider: React.FC<ApiProviderProps> = ({ children }) => {
    const [apiError, setApiError] = useState(false);

    // Restful react only automatically parses JSON responses if they have the content type application/json but
    // symfony returns 'application/problem+json' for it's errors so we need to parse it manually.
    function fixErrorData(headers: Headers, error: {data: any, [key: string]: any}) {
        if (!headers) {
            // Headers can be null on a network error, just ignore the error in this case
            return;
        }

        const contentType = headers.get('content-type');
        if (typeof error.data === 'string' && contentType && contentType.startsWith('application/problem+json')) {
            error.data = JSON.parse(error.data);
        }
    }

    return (
        <ApiErrorContext.Provider value={{
            apiError,
            setApiError,
        }}>
            {/* @ts-ignore */}
            <RestfulProvider
                base={dynamicConfig.atmUrl}
                onError={(error, retry, response) => fixErrorData(response?.headers as Headers, error)}
            >
                {children}
            </RestfulProvider>
        </ApiErrorContext.Provider>
    );
};

export default ApiProvider;
